import { Link } from "react-router-dom";
import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";
import { buttonLoadingCss } from "../../components/Button";
import { FormGroup } from "../../components/Form/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export { Container, Section } from "../Settings/styles";

/** "Carregar mais" (.secondary), and the buttons of the link's form and screen. */
export const CreateButton = styled.a`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  color: var(--color-neutral-white);
  background: var(--color-brand-blue);
  border-radius: 10px;

  &.secondary {
    align-self: center;
    background: var(--color-neutral-base);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

export const Filters = styled.div`
  display: flex;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;

  input {
    height: 40px;
    padding: 0 12px;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    color: var(--color-neutral-white);
    background: var(--color-neutral-dark);
    border: 1px solid var(--color-neutral-base);
    border-radius: 10px;
  }

  .search {
    flex: 1;
    min-width: 220px;
  }

  /* on a phone: the search on its own line, the period and the tag sharing the next */
  @media ${MOBILE} {
    .search {
      flex: 1 1 100%;
    }

    .period,
    .tag {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

/** As the Keepos of Meus Keepos: one under the other, "Criar link" first. */
export const List = styled.div`
  display: grid;
  /* minmax(0, …) lets long titles and addresses end in "…" instead of widening the column */
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  margin-bottom: 2rem;

  &[aria-busy="true"] {
    opacity: 0.6;
  }
`;

/** The first load: the shape of the screen (title, search and period, "Criar link", cards), each block in its place. */
export const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .title {
    width: 200px;
    height: 36px;
  }

  .filters {
    display: flex;
    gap: 0.75rem;
  }

  .search {
    flex: 1;
    height: 40px;
    border-radius: 10px;
  }

  .period {
    width: 150px;
    height: 40px;
    border-radius: 10px;
  }

  .create {
    height: 64px;
    border-radius: 1rem;
  }

  .card {
    height: 88px;
    border-radius: 1rem;
  }

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    .create {
      height: 72px;
    }
  }

  @media ${MOBILE} {
    .filters {
      flex-direction: column;
    }

    .period {
      width: 100%;
    }
  }
`;

/** The Keepo loading under the list while its next links come. */
export const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 2rem;
`;

/** The link's screen loading: back, its icon, title and "Editar", the two panels and the analytics, in their places. */
export const ViewSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .back {
    width: 110px;
    height: 18px;
  }

  .header {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .title {
    flex: 1;
    max-width: 320px;
    height: 36px;
  }

  .edit {
    width: 88px;
    height: 40px;
    margin-left: auto;
    border-radius: 10px;
  }

  .panels {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    gap: 0.75rem;
  }

  .panel {
    height: 200px;
    border-radius: 1rem;
  }

  .stats {
    height: 320px;
    border-radius: 1rem;
  }

  @media ${MOBILE} {
    .panels {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;

/** The link's form loading: its title, and the card with the four fields. */
export const FormSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .title {
    width: 180px;
    height: 36px;
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 24px;
    background: var(--color-neutral-dark);
    border-radius: 1rem;
  }

  .field {
    height: 64px;
    border-radius: 10px;
  }
`;

export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);

  button {
    font-weight: 700;
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

/** The link's form: one card, a field under the other (the panel's fields: label and count on top). */
export const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 20px 24px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  ${FormGroup} {
    margin-bottom: 0;
  }

  /* the Keepo symbol at the right end of a field while it's being filled (the field's svg margin is not for it) */
  .field-loader {
    display: flex;
    flex-shrink: 0;
    margin-right: 12px;

    svg {
      margin-right: 0;
    }
  }

  /* an example, not a value: light and thin */
  input::placeholder {
    font-weight: 400;
    color: var(--color-neutral-light);
    opacity: 1;
  }
`;

/** The path (kpo.bio/ inside the field) and "Gerar com IA" beside it, in line with the field. */
export const SlugRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;

  > ${FormGroup} {
    flex: 1;
    min-width: 0;
  }

  .ai {
    display: inline-flex;
    flex-shrink: 0;
    gap: 0.5rem;
    align-items: center;
    height: 40px;
    padding: 0 18px;
    margin-bottom: 8px;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: linear-gradient(135deg, #5b3df5, #8b5cf6);
    border-radius: 999px;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .labels {
      display: grid;

      > span {
        grid-area: 1 / 1;
        text-align: left;
      }

      .off {
        visibility: hidden;
      }
    }
  }

  /* the hint or error under the field pushes it up: the button follows the field, not the text */
  &:has(${FormGroup} > p) .ai {
    margin-bottom: calc(8px + 1.25rem);
  }

  @media ${MOBILE} {
    flex-direction: column;
    align-items: stretch;

    .ai {
      justify-content: center;
    }

    &:has(${FormGroup} > p) .ai {
      margin-bottom: 8px;
    }
  }
`;

/** The tags written, each with its "×", and the field to write one more: drawn as the other fields. */
export const TagsField = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
  min-height: 44px;
  padding: 6px 12px;
  margin: 8px 0;
  border: 2px solid var(--color-neutral-light);
  border-radius: 10px;

  &:focus-within {
    border-color: var(--color-brand-pink);
  }

  .tag {
    display: inline-flex;
    gap: 0.25rem;
    align-items: center;
    padding: 2px 4px 2px 10px;
    font-family: Inter, sans-serif;
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 999px;

    &::before {
      content: "#";
      opacity: 0.6;
    }

    button {
      display: grid;
      place-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
    }
  }

  input {
    flex: 1;
    min-width: 120px;
    height: 28px;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: transparent;
    border: 0;
    outline: none;
  }
`;

/** An error under its field (inside its FormGroup, which stacks upside down: order keeps it at the bottom). */
export const FieldError = styled.p`
  order: -1;
  margin-top: -0.25rem;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #ff8a80;
`;

/** The save bar's "Cancelar" (SaveBar's look) as a link back to the list. */
export const CancelLink = styled(Link)`
  display: flex;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-neutral-white);
`;

/** The save bar's main button (SaveBar's INFO LG), a real submit button. */
export const SaveButton = styled.button<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1rem;
  color: var(--color-neutral-white);
  background-color: var(--color-brand-blue);
  border-radius: 6.25rem;

  &:disabled {
    cursor: not-allowed;
  }

  ${props => props.$loading && buttonLoadingCss}
`;

/** "← Keepo Links", above the link. */
export const BackLink = styled(Link)`
  align-self: flex-start;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);

  &:hover {
    color: var(--color-neutral-white);
  }
`;

/** The link's screen: one block under the other. */
export const ViewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

/** The link's site, title, marks and "Editar"; on a phone, "Editar" under them. */
export const ViewHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;

  /* one line, "…" when long (the whole title on hover): "Editar" stays beside it */
  h1 {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* the site's icon, as on the list's card */
  > span:first-child {
    width: 48px;
    height: 48px;
    background: var(--color-neutral-dark);
  }

  .edit {
    margin-left: auto;
  }

  @media ${MOBILE} {
    .edit {
      flex: 1 1 100%;
      margin-left: 0;
    }
  }
`;

/** The link's information and its QR code: side by side, one under the other on a phone. */
export const ViewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 0.75rem;

  @media ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ViewPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
  padding: 24px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  h2 {
    font-family: Inter, sans-serif;
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1rem;
    color: var(--color-neutral-light);
    text-transform: uppercase;
    letter-spacing: 0.03125rem;
  }

  .hint {
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  /* the short link, big, and "Copiar" at the other end; a line under them */
  .short {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--color-neutral-base);

    /* one line, "…" when long ("Copiar" copies it whole) */
    .url {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      font-family: Inter, sans-serif;
      font-size: 1.25rem;
      font-weight: 700;
      line-height: 1.75rem;
      color: var(--color-neutral-white);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* Destino on its own line; Criado em and Tags side by side under it */
  dl {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem;
    padding-top: 0.25rem;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
  }

  .fact {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 0;

    &.wide {
      grid-column: 1 / -1;
    }
  }

  dt {
    font-size: 0.625rem;
    font-weight: 700;
    line-height: 1rem;
    color: var(--color-neutral-light);
    text-transform: uppercase;
    letter-spacing: 0.03125rem;
  }

  dd {
    min-width: 0;
    color: var(--color-neutral-white);

    a {
      display: flex;
      gap: 0.375rem;
      align-items: center;
      min-width: 0;
      color: var(--color-neutral-white);

      .text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* "open in a new tab", as on a Keepo's card */
      svg {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        color: var(--color-neutral-150);
      }

      &:hover .text {
        text-decoration: underline;
      }

      &:hover svg {
        color: var(--color-neutral-white);
      }
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .tag {
    padding: 2px 8px;
    font-size: 0.75rem;
    background: var(--color-neutral-base);
    border-radius: 999px;

    &::before {
      content: "#";
      opacity: 0.6;
    }
  }

  /* the code beside what it's for and its downloads; one under the other on a phone */
  .qrBody {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
  }

  /* the QR on white, so any camera reads it */
  .qr {
    flex-shrink: 0;
    width: 96px;
    height: 96px;
    padding: 6px;
    background: #fff;
    border-radius: 12px;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  /* beside the code while there's room for the buttons; under it when not */
  .qrSide {
    flex: 1 1 76px;
    min-width: 0;
  }

  /* one under the other, the same width */
  .qrActions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    /* the download icon and the format: "Baixar PNG" for screen readers */
    button {
      gap: 0.375rem;
      align-self: stretch;
      padding: 8px 12px;
      font-size: 0.8125rem;
      white-space: nowrap;
    }
  }

  @media ${MOBILE} {
    .qrBody {
      flex-direction: column;
      align-items: stretch;
    }

    .qr {
      align-self: center;
      width: 180px;
      height: 180px;
    }
  }
`;
